import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/directory", label: "Find a Surgeon" },
  { to: "/procedures", label: "Procedures", longLabel: "Cosmetic & Reconstructive" },
  { to: "/about", label: "The Society" },
  { to: "/news", label: "News" },
];

// Hide the navbar when scrolling down past `THRESHOLD_PX`, reveal when
// scrolling up. The mobile-menu open state pins it visible.
const THRESHOLD_PX = 80;
const DELTA_PX = 6;

function useHideOnScroll(disabled = false) {
  const [hidden, setHidden] = useState(false);
  const lastYRef = useRef(typeof window === "undefined" ? 0 : window.scrollY);
  const tickingRef = useRef(false);

  useEffect(() => {
    if (disabled) {
      setHidden(false);
      return;
    }
    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastYRef.current;
        if (y < THRESHOLD_PX) {
          setHidden(false);
        } else if (Math.abs(delta) > DELTA_PX) {
          setHidden(delta > 0);
        }
        lastYRef.current = y;
        tickingRef.current = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [disabled]);

  return hidden;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Disable hide-on-scroll when the mobile menu is open
  const hidden = useHideOnScroll(open);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      style={{
        transform: hidden ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "transform",
      }}
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-fscs-border"
    >
      <div className="px-4 sm:px-5 md:px-6 lg:px-12 xl:px-20 2xl:px-28 py-3 lg:py-4 flex items-center gap-4 lg:gap-6">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <img
            src="/assets/logo-white.png"
            alt="Florida Cosmetic Surgeons"
            className="h-12 sm:h-14 lg:h-16 w-auto"
          />
        </Link>

        {/* Inline nav - visible from md (768px) up, centered between logo & auth */}
        <nav className="hidden md:flex items-center gap-3 lg:gap-5 xl:gap-7 ml-auto md:mr-2 lg:mr-0">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-[12px] lg:text-[13px] xl:text-sm tracking-wide transition-colors whitespace-nowrap ${
                  isActive ? "text-fscs-coral" : "text-fscs-ink hover:text-fscs-coral"
                }`
              }
            >
              <span className={l.longLabel ? "xl:hidden" : ""}>{l.label}</span>
              {l.longLabel && (
                <span className="hidden xl:inline">{l.longLabel}</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Auth area - Join always shown md+; "Surgeon Login" text from lg+ */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0 ml-auto md:ml-3 lg:ml-6">
          {user && typeof user === "object" ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className="hidden lg:inline-block text-sm text-fscs-coral hover:text-fscs-ink whitespace-nowrap font-semibold"
                >
                  Admin
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="hidden lg:inline-block text-sm text-fscs-ink hover:text-fscs-coral whitespace-nowrap"
              >
                Dashboard
              </Link>
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="btn-outline-ink text-[12px] lg:text-sm whitespace-nowrap !py-2 !px-3 lg:!py-2.5 lg:!px-4"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="hidden lg:inline-block text-sm text-fscs-ink hover:text-fscs-coral whitespace-nowrap"
              >
                Surgeon Login
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="btn-coral text-[12px] lg:text-sm whitespace-nowrap !py-2 !px-3 lg:!py-2.5 lg:!px-4"
              >
                <span className="lg:hidden">Join</span>
                <span className="hidden lg:inline">Join the Society</span>
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu toggle - only visible below md */}
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-fscs-ink p-2 -mr-2 ml-auto"
          onClick={() => setOpen(!open)}
          aria-label="toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-fscs-border bg-white">
          <div className="px-4 sm:px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                className="text-fscs-ink text-base"
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-fscs-border pt-5 flex flex-col gap-4">
              {user && typeof user === "object" ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    className="text-fscs-ink text-base"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="btn-outline-ink text-sm w-fit"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="text-fscs-ink text-base"
                  >
                    Surgeon Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="btn-coral text-sm w-fit"
                  >
                    Join the Society
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

